import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/hooks/useProfile';

export const useCustomTypes = (userId) => {
    const [customTypes, setCustomTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [sortBy, setSortBy] = useState('created_desc');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { fetchProfile } = useProfile();

    useEffect(() => {
        if (userId) {
            fetchCustomTypes();

            // Set up real-time subscription
            const subscription = supabase
                .channel('custom_analysis_types')
                .on('postgres_changes',
                    {
                        event: '*',
                        schema: 'public',
                        table: 'custom_analysis_types',
                        filter: `user_id=eq.${userId}`
                    },
                    () => {
                        fetchCustomTypes();
                    }
                )
                .subscribe();

            // Cleanup subscription on unmount
            return () => {
                subscription.unsubscribe();
            };
        }
    }, [userId]);

    const fetchCustomTypes = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('custom_analysis_types')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setCustomTypes(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching custom types:', err);
            setError('Failed to load custom analysis types');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setDeleteLoading(id);
        try {
            const { error } = await supabase
                .from('custom_analysis_types')
                .delete()
                .eq('id', id)
                .eq('user_id', userId);

            if (error) throw error;
            setCustomTypes(prevTypes => prevTypes.filter(type => type.id !== id));
            setError(null);
            return true;
        } catch (err) {
            console.error('Error deleting custom type:', err);
            setError('Failed to delete custom analysis type');
            return false;
        } finally {
            setDeleteLoading(null);
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        fetchCustomTypes();
        fetchProfile();
    };

    const sortedTypes = useMemo(() => {
        if (!customTypes) return [];

        return [...customTypes].sort((a, b) => {
            switch (sortBy) {
                case 'created_asc':
                    return new Date(a.created_at) - new Date(b.created_at);
                case 'created_desc':
                    return new Date(b.created_at) - new Date(a.created_at);
                case 'last_used_asc':
                    if (!a.last_used_at && !b.last_used_at) return 0;
                    if (!a.last_used_at) return 1;
                    if (!b.last_used_at) return -1;
                    return new Date(a.last_used_at) - new Date(b.last_used_at);
                case 'last_used_desc':
                    if (!a.last_used_at && !b.last_used_at) return 0;
                    if (!a.last_used_at) return 1;
                    if (!b.last_used_at) return -1;
                    return new Date(b.last_used_at) - new Date(a.last_used_at);
                case 'name_asc':
                    return a.name.localeCompare(b.name);
                case 'name_desc':
                    return b.name.localeCompare(a.name);
                default:
                    return 0;
            }
        });
    }, [customTypes, sortBy]);

    return {
        sortedTypes,
        customTypes,
        loading,
        error,
        deleteLoading,
        isModalOpen,
        sortBy,
        handleDelete,
        handleModalClose,
        setIsModalOpen,
        setSortBy,
        fetchCustomTypes
    };
};